import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Heart, Bookmark, FileText, Image as ImageIcon, FileCode } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { CURRICULA, SUBJECT_NAMES, getCurriculum } from '../constants/curricula';
import {
  hasLocalLike, addLocalLike,
  hasLocalSave, toggleLocalSave,
} from '../lib/localUserState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFileIcon = (fileType) => {
  if (fileType === 'pdf') return <FileText className="h-5 w-5" strokeWidth={1.5} />;
  if (['png', 'jpg', 'jpeg'].includes(fileType)) return <ImageIcon className="h-5 w-5" strokeWidth={1.5} />;
  return <FileCode className="h-5 w-5" strokeWidth={1.5} />;
};

export const Resources = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [curriculum, setCurriculum] = useState(searchParams.get('curriculum') || 'All');
  const [level, setLevel] = useState(searchParams.get('level') || 'All');
  const [subject, setSubject] = useState(searchParams.get('subject') || 'All');
  // Local maps of which resources the current device has liked/saved
  const [likedMap, setLikedMap] = useState({});
  const [savedMap, setSavedMap] = useState({});

  const currObj = getCurriculum(curriculum);
  const showLevelFilter = currObj && Array.isArray(currObj.levels);

  useEffect(() => {
    fetchResources();
  }, [searchParams]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = {};
      const searchQuery = searchParams.get('search');
      const subjectQuery = searchParams.get('subject');
      const curriculumQuery = searchParams.get('curriculum');
      const levelQuery = searchParams.get('level');

      if (searchQuery) params.search = searchQuery;
      if (subjectQuery && subjectQuery !== 'All') params.subject = subjectQuery;
      if (curriculumQuery && curriculumQuery !== 'All') params.curriculum = curriculumQuery;
      if (levelQuery && levelQuery !== 'All') params.level = levelQuery;

      const { data } = await axios.get(`${API}/resources`, { params });
      setResources(data);
      // Sync local liked/saved state from localStorage
      const liked = {};
      const saved = {};
      data.forEach((r) => {
        liked[r.id] = hasLocalLike(r.id);
        saved[r.id] = hasLocalSave(r.id);
      });
      setLikedMap(liked);
      setSavedMap(saved);
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (overrides = {}) => {
    const next = {
      search: overrides.search ?? search,
      curriculum: overrides.curriculum ?? curriculum,
      level: overrides.level ?? level,
      subject: overrides.subject ?? subject,
    };
    const params = {};
    if (next.search) params.search = next.search;
    if (next.curriculum && next.curriculum !== 'All') params.curriculum = next.curriculum;
    const nextCurr = getCurriculum(next.curriculum);
    if (next.level && next.level !== 'All' && nextCurr && nextCurr.levels) params.level = next.level;
    if (next.subject && next.subject !== 'All') params.subject = next.subject;
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handleCurriculumChange = (val) => {
    setCurriculum(val);
    const c = getCurriculum(val);
    const newLevel = c && c.levels ? level : 'All';
    setLevel(newLevel);
    applyFilters({ curriculum: val, level: newLevel });
  };

  const handleSubjectChange = (val) => {
    setSubject(val);
    applyFilters({ subject: val });
  };

  const handleLevelChange = (val) => {
    setLevel(val);
    applyFilters({ level: val });
  };

  const handleLike = async (resourceId) => {
    if (hasLocalLike(resourceId)) {
      toast.info('You already liked this');
      return;
    }
    try {
      await axios.post(`${API}/resources/${resourceId}/like`);
      addLocalLike(resourceId);
      setLikedMap((m) => ({ ...m, [resourceId]: true }));
      setResources((rs) => rs.map((r) =>
        r.id === resourceId ? { ...r, likes_count: r.likes_count + 1 } : r
      ));
    } catch (error) {
      toast.error('Failed to like resource');
    }
  };

  const handleSave = (resourceId) => {
    const nowSaved = toggleLocalSave(resourceId);
    setSavedMap((m) => ({ ...m, [resourceId]: nowSaved }));
    toast.success(nowSaved ? 'Saved on this device' : 'Removed from saved');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-semibold tracking-tight mb-6">Browse Resources</h1>

          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-foreground/60" strokeWidth={1.5} />
              <Input
                type="text"
                placeholder="Search resources..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="resources-search-input"
              />
            </div>
            <Button type="submit" data-testid="resources-search-button">Search</Button>
          </form>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Select value={curriculum} onValueChange={handleCurriculumChange}>
              <SelectTrigger data-testid="resources-curriculum-filter">
                <SelectValue placeholder="All Curricula" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All" data-testid="resources-curr-all">All Curricula</SelectItem>
                {CURRICULA.map((c) => (
                  <SelectItem key={c.name} value={c.name} data-testid={`resources-curr-${c.name.toLowerCase()}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {showLevelFilter ? (
              <Select value={level} onValueChange={handleLevelChange}>
                <SelectTrigger data-testid="resources-level-filter">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All" data-testid="resources-level-all">All Levels ({currObj.levels.join(' + ')})</SelectItem>
                  {currObj.levels.map((lvl) => (
                    <SelectItem key={lvl} value={lvl} data-testid={`resources-level-${lvl}`}>{lvl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="hidden sm:block lg:block" />
            )}

            <Select value={subject} onValueChange={handleSubjectChange}>
              <SelectTrigger data-testid="resources-subject-filter">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All" data-testid="resources-subj-all">All Subjects</SelectItem>
                {SUBJECT_NAMES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`resources-subj-${s.toLowerCase().replace(/\s+/g, '-')}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-20" data-testid="resources-empty-state">
            <div className="w-20 h-20 mx-auto rounded-full bg-accent flex items-center justify-center mb-6">
              <FileText className="h-10 w-10 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold mb-2">No resources found</h3>
            <p className="text-foreground/70 mb-6">Try adjusting your filters or be the first to upload one.</p>
            <Link to="/upload">
              <Button data-testid="resources-upload-first">Upload First Resource</Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
            {resources.map((resource) => {
              const rCurr = getCurriculum(resource.curriculum);
              const isLiked = likedMap[resource.id];
              const isSaved = savedMap[resource.id];
              return (
                <div
                  key={resource.id}
                  className="border rounded-lg p-6 hover:-translate-y-0.5 transition-all duration-200 hover:shadow-sm bg-surface"
                  data-testid={`resource-card-${resource.id}`}
                >
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-primary">{getFileIcon(resource.file_type)}</span>
                      {rCurr && (
                        <span className={`text-xs px-2 py-0.5 rounded font-mono ${rCurr.badge}`}>
                          {resource.curriculum}{resource.level ? ` · ${resource.level}` : ''}
                        </span>
                      )}
                      <span className="text-xs uppercase tracking-wider font-mono text-foreground/70">{resource.subject}</span>
                    </div>
                  </div>

                  <Link to={`/resources/${resource.id}`}>
                    <h3 className="text-lg font-semibold mb-2 hover:text-primary transition-colors" data-testid={`resource-title-${resource.id}`}>
                      {resource.title}
                    </h3>
                  </Link>

                  {resource.description && (
                    <p className="text-sm text-foreground/75 mb-4 line-clamp-2 leading-relaxed">{resource.description}</p>
                  )}

                  {resource.tags && resource.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {resource.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded bg-accent text-foreground/75" data-testid={`resource-tag-${tag}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                        {(resource.uploader_name || 'A')[0].toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-foreground/85">{resource.uploader_name || 'Anonymous'}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLike(resource.id)}
                        className={isLiked ? 'text-red-500' : ''}
                        data-testid={`resource-like-${resource.id}`}
                      >
                        <Heart className="h-4 w-4 mr-1" strokeWidth={1.5} fill={isLiked ? 'currentColor' : 'none'} />
                        {resource.likes_count}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSave(resource.id)}
                        className={isSaved ? 'text-primary' : ''}
                        data-testid={`resource-save-${resource.id}`}
                      >
                        <Bookmark className="h-4 w-4" strokeWidth={1.5} fill={isSaved ? 'currentColor' : 'none'} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
